import { Emoji } from '@gagovictor/shared-frontend-core/dist/shared/components/Emoji';
import { Box } from '@mui/material';
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import { useT } from '../i18n';

import { CompletionId, Look } from './catalog';
import { playCue } from './feedback';
import { TomatoScene } from './scene/TomatoScene';

export interface TomatoHandle {
    playCompletion: (kind: CompletionId) => Promise<void>;
    hype: () => void;
    celebrate: () => void;
    poke: () => void;
    bounce: (strength: number) => void;
    yawn: () => void;
    spook: () => void;
    music: (level: number, beat: boolean) => void;
}

interface Props {
    look: Look;
    progress: number;
    /** The session length, for the kitchen-timer dial. */
    totalMs: number;
    running: boolean;
    /** Leave room at the bottom for the timer panel. */
    reserveBottom: boolean;
    /** Light or dark theme, which picks the day or night version of the scene. */
    dark: boolean;
}

/** Owns the WebGL canvas. Falls back to an emoji where WebGL is unavailable. */
export const TomatoCanvas = forwardRef<TomatoHandle, Props>(
    ({ look, progress, totalMs, running, reserveBottom, dark }, ref) => {
        const t = useT();
        const host = useRef<HTMLDivElement>(null);
        const scene = useRef<TomatoScene | null>(null);
        const [failed, setFailed] = useState(false);
        const latest = useRef({
            look,
            progress,
            totalMs,
            running,
            reserveBottom,
        });
        latest.current = { look, progress, totalMs, running, reserveBottom };

        useEffect(() => {
            if (!host.current) return;
            let created: TomatoScene;
            try {
                created = new TomatoScene(host.current, {
                    onCue: playCue,
                    onPoke: () => playCue('poke', 0.9 + Math.random() * 0.3),
                });
            } catch {
                setFailed(true);
                return;
            }
            scene.current = created;
            created.setLook(latest.current.look);
            created.setReserve(latest.current.reserveBottom ? 0.32 : 0);
            created.setProgress(
                latest.current.progress,
                latest.current.running,
                latest.current.totalMs
            );
            return () => {
                created.dispose();
                scene.current = null;
            };
        }, []);

        useEffect(() => scene.current?.setLook(look), [look]);
        const farmSign = t('scene.farmSign');
        useEffect(
            () => scene.current?.setBackdrop(look.background, dark, farmSign),
            [look.background, dark, farmSign]
        );
        useEffect(
            () => scene.current?.setProgress(progress, running, totalMs),
            [progress, running, totalMs]
        );
        useEffect(
            () => scene.current?.setReserve(reserveBottom ? 0.32 : 0),
            [reserveBottom]
        );

        useImperativeHandle(
            ref,
            () => ({
                playCompletion: async (kind) => {
                    // Without WebGL there is nothing to animate; keep the flow moving.
                    if (!scene.current)
                        return new Promise((r) => setTimeout(r, 800));
                    await scene.current.playCompletion(kind);
                },
                hype: () => scene.current?.hype(),
                celebrate: () => scene.current?.celebrate(),
                poke: () => scene.current?.poke(),
                bounce: (strength) => scene.current?.bounce(strength),
                yawn: () => void scene.current?.yawn(),
                spook: () => scene.current?.spook(),
                music: (level, beat) => scene.current?.music(level, beat),
            }),
            []
        );

        return (
            <Box
                ref={host}
                sx={{ position: 'absolute', inset: 0 }}
                aria-label={t('app.name')}
                role="img"
            >
                {failed && (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'grid',
                            placeItems: 'center',
                        }}
                    >
                        <Emoji emoji="🍅" size={120} />
                    </Box>
                )}
            </Box>
        );
    }
);
TomatoCanvas.displayName = 'TomatoCanvas';
